import React from 'react';
import Modal from '../Modal/Modal';
import { CycleController } from '../../store/models/controllers';
import { useFormik } from 'formik';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { Dispatch } from '../../store/store';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { ICustomChannelName } from '../../types/channel-types';
import Table from '../Table/Table';

import './ReportChannelsModal.css';
import { DeviceColor } from '../CycleChartPreview/types';
import { CHARACTER_SEPARATOR_DEVICE_NAME } from '../CycleChartPreview/constants';
import HorizontalAlignmentDirections from '../../enums/horizontal-alignment-directions.enum';

export interface IExcludedChannelNames {
  controllerId: string;
  excludeChannels: string[];
  excludeThermocouples: string[];
}

export interface IUpdateExclusionData {
  controllerId: string;
  excludeChannels: string[];
  excludeThermocouples: string[];
}

export interface TUpdateColorData {
  controllerId: string;
  channelColors: DeviceColor[];
  thermocoupleColors: DeviceColor[];
}

export interface IColorsDevices {
  controllerId: string;
  channelColors: DeviceColor[];
  thermocoupleColors: DeviceColor[];
  controller_serial_number?: string;
}

type TReportChannelsModalProps = {
  cycleId: string;
  onOpen: () => void;
  onClose: () => void;
  fetchChartSettings: (id: string) => Promise<void>
  cycleControllers: CycleController[],
  excludedDevices: IExcludedChannelNames[],
  channelNames: ICustomChannelName[];
  colorsDevices: IColorsDevices[];
} & ReturnType<typeof mapDispatch>;;

interface IExcludeChannelsFormValues {
  exclusions: IUpdateExclusionData[];
  colors: IColorsDevices[];
}

type TableColumn = {
  name: string,
  isIncluded: string,
  color: string,
}

type TableHeadAlignment = {
  [key in TableColumn[keyof TableColumn]]: HorizontalAlignmentDirections;
};

const DEFAULT_COLOR = '#000000';

const ReportChannelsModal: React.FC<TReportChannelsModalProps> = ({
  cycleId,
  cycleControllers,
  excludedDevices,
  onClose,
  updateExcludeChannelNames,
  updateDeviceColorsReport,
  fetchChartSettings,
  channelNames,
  colorsDevices,
}) => {
  const { t } = useTranslation();

  const tableColumn: TableColumn = {
    name: t('commonText.name'),
    isIncluded: t('commonText.isIncluded'),
    color: t('commonText.color'),
  }
  const tableHeadAlignment: TableHeadAlignment = {
    [tableColumn.name]: HorizontalAlignmentDirections.Left,
    [tableColumn.isIncluded]: HorizontalAlignmentDirections.Center,
    [tableColumn.color]: HorizontalAlignmentDirections.Center,
  };
  const tableHeads = Object.values(tableColumn);

  const updateColorByDevice = async (cycleId: string, listData: IColorsDevices[]) => {
    const modifiedData = listData.map((item) => {
      const { controllerId } = item;

      const channelColors = item.channelColors.filter((deviceWithColor) => Object.values(deviceWithColor)[0] !== null);
      const thermocoupleColors = item.thermocoupleColors.filter((deviceWithColor) => Object.values(deviceWithColor)[0] !== null);

      return {
        controllerId,
        channelColors,
        thermocoupleColors,
      };
    });

    await updateDeviceColorsReport({ cycleId, data: modifiedData });

    return;
  }

  const excludeChannelsFormik = useFormik<IExcludeChannelsFormValues>({
    initialValues: {
      exclusions: cycleControllers.map((cycleController) => {
        const exclusion = excludedDevices.find((e) => e.controllerId === cycleController.controllerId);

        const channels = exclusion?.excludeChannels || [];
        const thermocouples = exclusion?.excludeThermocouples || [];

        return {
          controllerId: cycleController.controllerId,
          excludeChannels: channels,
          excludeThermocouples: thermocouples,
        };
      }),
      colors: cycleControllers.map((cycleController) => {
        const colorsByDevices = colorsDevices.find((e) => e.controllerId === cycleController.controllerId);

        const channelColors = colorsByDevices?.channelColors || [];
        const thermocoupleColors = colorsByDevices?.thermocoupleColors || [];

        return {
          controllerId: cycleController.controllerId,
          channelColors,
          thermocoupleColors,
        };
      }),
    },
    onSubmit: async ({ exclusions, colors }) => {
      await updateExcludeChannelNames({ cycleId, data: exclusions });
      await updateColorByDevice(cycleId, colors);
      await fetchChartSettings(cycleId);
      toast.success(t('messages.updateExcludeChannelSuccess'));
      onClose();
    },
  });

  const handleCheckboxChange = (
    type: 'excludeChannels' | 'excludeThermocouples',
    idx: number,
    value: string,
    isChecked: boolean,
  ) => {
    const path = `exclusions[${idx}].${type}`;
    let updatedValues = [...excludeChannelsFormik.values.exclusions[idx][type]];

    if (isChecked) {
      updatedValues.push(value);
    } else {
      updatedValues = updatedValues.filter((item) => item !== value);
    }

    excludeChannelsFormik.setFieldValue(path, updatedValues);
  };

  const changeColor = (event: React.ChangeEvent<HTMLInputElement>, type: 'channelColors' | 'thermocoupleColors', idx: number,) => {
    const { name, value } = event.target;

    const path = `colors[${idx}].${type}`;
    const colors = excludeChannelsFormik.values.colors[idx][type];

    const newList = colors.map((device) => name in device ? ({ [name]: value }) : device);

    excludeChannelsFormik.setFieldValue(path, newList);
  };

  const getControllerRows = (controllerItem: CycleController, idx: number) => {
    const { channels, thermocouples, controller, } = controllerItem;
    const exclusion = excludeChannelsFormik.values.exclusions[idx];
    const colors = excludeChannelsFormik.values.colors[idx];
    const serialNumber = controller.serial_number;
    const controllerName = controller?.name || serialNumber;

    const getLabel = (base: string, id: string, controllerNameOrSN?: string) =>
      `${base}-${id.split(CHARACTER_SEPARATOR_DEVICE_NAME)[1]} (${controllerNameOrSN})`;

    const rows: ({ [key in TableColumn[keyof TableColumn]]: JSX.Element | string } & { id: string })[] = [];

    // Add channel rows
    channels.forEach((channel) => {
      const isChecked = !exclusion.excludeChannels.includes(channel);
      const fullName = getLabel('Channel', channel, controllerName);
      const displayName =
        channelNames.find((n) => n.original === fullName)?.fromDynamo || fullName;

      const finedColor = colors.channelColors.find((item) => channel in item);
      const color = finedColor ? finedColor[channel] : '';

      rows.push({
        id: displayName,
        [tableColumn.name]: (<span className='inline-block w-full text-left'>{displayName}</span>),
        [tableColumn.isIncluded]: (
          <span className='flex justify-center items-center'>
            <input
              className="h-5 w-5"
              type="checkbox"
              checked={isChecked}
              onChange={() =>
                handleCheckboxChange('excludeChannels', idx, channel, isChecked)
              }
            />
          </span>
        ),
        [tableColumn.color]: (
          <span className='flex justify-center items-center rounded'>
            <span className='inline-block rounded w-5 h-5 border-2 border-solid overflow-hidden'>
              <input
                className={`cursor-pointer relative inline-block ${color ? '' : 'opacity-0'}`}
                type="color"
                name={channel}
                value={color ?? DEFAULT_COLOR}
                onChange={(e) => changeColor(e, 'channelColors', idx)}
              />
            </span>
          </span>
        )
      });
    });

    // Add thermocouple rows
    thermocouples.forEach((tc) => {
      const isChecked = !exclusion.excludeThermocouples.includes(tc);
      const label = getLabel('Thermocouple', tc, controllerName);

      const finedColor = colors.thermocoupleColors.find((item) => tc in item);
      const color = finedColor ? finedColor[tc] : '';

      rows.push({
        id: label,
        [tableColumn.name]: <span className='inline-block w-full text-left'>{label}</span>,
        [tableColumn.isIncluded]: (
          <input
            className="h-5 w-5"
            type="checkbox"
            checked={isChecked}
            onChange={() =>
              handleCheckboxChange('excludeThermocouples', idx, tc, isChecked)
            }
          />
        ),
        [tableColumn.color]: (
          <span className='flex justify-center items-center rounded'>
            <span className='inline-block rounded w-5 h-5 border-2 border-solid overflow-hidden'>
              <input
                className={`cursor-pointer relative inline-block ${color ? '' : 'opacity-0'}`}
                type="color"
                name={tc}
                value={color ?? DEFAULT_COLOR}
                onChange={(e) => changeColor(e, 'thermocoupleColors', idx)}
              />
            </span>
          </span>
        )
      });
    });

    return rows;
  };

  const viewValues = cycleControllers.flatMap((controllerItem, idx) => {
    const rows = getControllerRows(controllerItem, idx);
    return rows;
  });

  const submitFormByBtn = (e) => {
    e.preventDefault();
    excludeChannelsFormik.submitForm();
  };

  return (
    <Modal
      modalTitle={t('labels.chartSettings')}
      isVisible={true}
      onClose={onClose}
      showAcceptBtn={false}
      showCancelBtn={false}
      className='sm:min-w-fit'
    >
      <form onSubmit={submitFormByBtn} className="c-report-channels">
        <Table values={viewValues} keyAlign={tableHeadAlignment} defaultKeys={tableHeads} />
        <InteractiveButton
          text={t('buttons.save')}
          submit
          onClick={(e) => {
            e.preventDefault();
            excludeChannelsFormik.submitForm();
          }}
          isAdaptive
          className="w-full mt-4"
        />
      </form>
    </Modal>
  )
};


const mapDispatch = (dispatch: Dispatch) => ({
  updateExcludeChannelNames: dispatch.cycles.updateExcludeChannelNames,
  updateDeviceColorsReport: dispatch.cycles.updateDeviceColorsReport,
});

export default connect(null, mapDispatch)(ReportChannelsModal);
